// frontend/src/services/deviceSignals.js
//
// The device beacon: a small, first-party record of WHICH browser on WHICH
// device a signed-in account is using, so the admin can tell when a "new" trial
// account is really an earlier one again (repeat-trial detection).
//
// WHAT IT IS NOT
// --------------
// - Not product analytics. It is fraud prevention, so it is not gated on the
//   analytics consent - and for the same reason it never feeds analytics, never
//   goes to a third party and loads no third-party code. The only recipient is
//   our own API (`POST /users/me/device-signals`).
// - Not a fingerprinting kit. No canvas or audio rendering, no font probing.
//   It reads values the browser already exposes (screen, timezone, languages,
//   core count, the GPU name via WEBGL_debug_renderer_info, User-Agent client
//   hints) plus one random id this site stores for itself (`lf_did`, renewed
//   after 24 months - see DEVICE_ID_MAX_AGE_MS).
//
// THE CONTRACT WITH THE BACKEND
// -----------------------------
// The server takes ALLOWLISTED keys only and answers 422 to anything else, so
// every key below is spelled exactly as the spec's allowlist, strings are
// truncated to the spec's lengths, and numbers outside a sane range are dropped
// rather than sent. IP, User-Agent and Accept-Language are read by the server
// from the request itself - never sent in the body.
//
// THE EMAIL DOOR
// --------------
// An email sign-up holds no session until the user later logs in (the code
// page sends them to /login), so the authenticated beacon cannot run after the
// code. `collectDeviceSignalsBody` gives the verify page the same fields to
// send WITH `POST /auth/verify-email`, which records them server-side.
//
// SAFETY
// ------
// `scheduleDeviceSignals` is fire-and-forget: it returns only a cancel
// function, never throws, never awaits on the caller's path and runs only when
// the browser is idle. Cancelling (a sign-out before the send) stops it up to
// the moment the request goes out.
// Every storage access sits in try/catch; with no storage it still sends, just
// without a device id and without the 12-hour throttle (an in-memory guard
// still stops a second send in the same page load).

import api from "./api";

/**
 * localStorage key of this browser's random device id. The value is JSON
 * `{"id": "<uuid v4>", "created": <epoch ms>}`; a bare uuid (the first shape)
 * is still read and is rewritten in the JSON shape on the next read.
 */
export const DEVICE_ID_KEY = "lf_did";

/**
 * The longest a device id lives before a fresh one replaces it: 24 months, the
 * upper bound the cookie policy gives essential storage ("session or up to 24
 * months"). Counted as 730 days.
 */
export const DEVICE_ID_MAX_AGE_MS = 730 * 24 * 60 * 60 * 1000;

/** localStorage key of the throttle map `{ [userId]: lastSentEpochMs }`. */
export const THROTTLE_KEY = "lf_dsig_sent";

/** One send per user per device per 12 hours. */
export const THROTTLE_MS = 12 * 60 * 60 * 1000;

/** How long to wait for the high-entropy client hints before going without. */
export const CLIENT_HINTS_TIMEOUT_MS = 500;

/** Most throttle entries kept (a shared family computer, an admin testing). */
const THROTTLE_MAX_ENTRIES = 20;

/** The `context` values the server accepts. */
export const SIGNAL_CONTEXTS = Object.freeze(["signup", "login", "session"]);

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** High-entropy hints requested from `navigator.userAgentData`. */
const HIGH_ENTROPY_HINTS = Object.freeze([
  "model",
  "platform",
  "platformVersion",
  "architecture",
  "bitness",
  "uaFullVersion",
]);

const HINT_STRING_MAX = 64;
const MAX_BRANDS = 6;

/** Users already sent (or in flight) in THIS page load - the storage-less guard. */
const sentThisPageLoad = new Set();

// --- storage -------------------------------------------------------------------

function storageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/** A v4 uuid: `crypto.randomUUID`, else `getRandomValues`, else null. */
export function generateUuidV4() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const b = crypto.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
      return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    }
  } catch {
    /* no usable crypto - fall through */
  }
  // No CSPRNG: send no id rather than a guessable one.
  return null;
}

/**
 * Parse the stored device-id value. Returns `{ id, created }` (created is null
 * for the legacy bare-uuid shape) or null when the value is unusable.
 */
function parseStoredDeviceId(raw) {
  if (!raw) return null;
  if (UUID_V4.test(raw)) return { id: raw.toLowerCase(), created: null };
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.id === "string" &&
      UUID_V4.test(parsed.id) &&
      Number.isFinite(parsed.created)
    ) {
      return { id: parsed.id.toLowerCase(), created: parsed.created };
    }
  } catch {
    /* not JSON - unusable */
  }
  return null;
}

/** Write `{ id, created }` and read it back; the id, or null when it did not persist. */
function persistDeviceId(id, created) {
  const value = JSON.stringify({ id, created });
  if (!storageSet(DEVICE_ID_KEY, value)) return null;
  // Read back: a storage that accepts writes but returns nothing is not a store.
  return storageGet(DEVICE_ID_KEY) === value ? id : null;
}

/**
 * This browser's device id, created on first use and replaced by a fresh one
 * once it is older than DEVICE_ID_MAX_AGE_MS (or stamped in the future, which
 * would never expire). Null when storage is not usable
 * (private mode, blocked site data) - an id that cannot persist would be a new
 * "device" on every load, which is worse than none.
 *
 * @param {number} [now] epoch ms (tests)
 * @returns {string|null}
 */
export function getOrCreateDeviceId(now = Date.now()) {
  const stored = parseStoredDeviceId(storageGet(DEVICE_ID_KEY));
  if (stored && stored.created === null) {
    // Legacy bare uuid: keep the id, start its clock now. If the rewrite does
    // not stick, the old value is still there and still the id.
    persistDeviceId(stored.id, now);
    return stored.id;
  }
  // A `created` in the future is invalid (it would never expire): regenerate.
  if (stored && stored.created <= now && now - stored.created < DEVICE_ID_MAX_AGE_MS) {
    return stored.id;
  }
  const fresh = generateUuidV4();
  if (!fresh) return null;
  return persistDeviceId(fresh, now);
}

function readThrottleMap() {
  const raw = storageGet(THROTTLE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/** True when this user already sent from this browser in the last 12 hours. */
export function isThrottled(userId, now = Date.now()) {
  const last = Number(readThrottleMap()[userId]);
  return Number.isFinite(last) && last <= now && now - last < THROTTLE_MS;
}

/** Stamp a send for `userId`, dropping expired and surplus entries. */
export function markSent(userId, now = Date.now()) {
  const map = readThrottleMap();
  map[userId] = now;
  const live = Object.entries(map)
    .filter(([, ts]) => Number.isFinite(Number(ts)) && now - Number(ts) < THROTTLE_MS)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, THROTTLE_MAX_ENTRIES);
  storageSet(THROTTLE_KEY, JSON.stringify(Object.fromEntries(live)));
}

// --- collection ------------------------------------------------------------------

function str(value, max) {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : undefined;
}

/** A finite number inside [min, max], else undefined (never sent). */
function num(value, min, max, { integer = false } = {}) {
  const n = typeof value === "number" ? value : Number.NaN;
  if (!Number.isFinite(n) || n < min || n > max) return undefined;
  return integer ? Math.round(n) : Math.round(n * 1000) / 1000;
}

function bool(value) {
  return typeof value === "boolean" ? value : undefined;
}

/** Drop undefined keys; null when nothing is left. */
function compact(obj) {
  const out = {};
  Object.keys(obj).forEach((k) => {
    if (obj[k] !== undefined) out[k] = obj[k];
  });
  return Object.keys(out).length ? out : null;
}

/**
 * Shape the client hints to the server allowlist.
 * @param {object|null} raw values from userAgentData (low + high entropy)
 * @returns {object|null}
 */
export function shapeClientHints(raw) {
  if (!raw || typeof raw !== "object") return null;
  const brands = Array.isArray(raw.brands)
    ? raw.brands
        .slice(0, MAX_BRANDS)
        .map((b) => compact({ brand: str(b?.brand, HINT_STRING_MAX), version: str(b?.version, HINT_STRING_MAX) }))
        .filter((b) => b && b.brand)
    : undefined;
  return compact({
    model: str(raw.model, HINT_STRING_MAX),
    platform: str(raw.platform, HINT_STRING_MAX),
    platformVersion: str(raw.platformVersion, HINT_STRING_MAX),
    mobile: bool(raw.mobile),
    architecture: str(raw.architecture, HINT_STRING_MAX),
    bitness: str(raw.bitness, HINT_STRING_MAX),
    uaFullVersion: str(raw.uaFullVersion, HINT_STRING_MAX),
    brands: brands && brands.length ? brands : undefined,
  });
}

/**
 * Low-entropy hints at once, high-entropy ones if they arrive within
 * `timeoutMs` (default 500 ms). Never rejects; null where the API does not
 * exist (Firefox, Safari).
 */
export async function collectClientHints(
  nav = typeof navigator !== "undefined" ? navigator : undefined,
  { timeoutMs = CLIENT_HINTS_TIMEOUT_MS } = {}
) {
  let uad;
  try {
    uad = nav?.userAgentData;
  } catch {
    return null;
  }
  if (!uad) return null;
  const base = { brands: uad.brands, mobile: uad.mobile, platform: uad.platform };
  let high = null;
  if (typeof uad.getHighEntropyValues === "function") {
    let timer;
    try {
      high = await Promise.race([
        Promise.resolve(uad.getHighEntropyValues([...HIGH_ENTROPY_HINTS])),
        new Promise((resolve) => {
          timer = setTimeout(() => resolve(null), timeoutMs);
        }),
      ]);
    } catch {
      high = null;
    } finally {
      clearTimeout(timer);
    }
  }
  return shapeClientHints({ ...base, ...(high || {}) });
}

/**
 * The GPU vendor and renderer names, read WITHOUT drawing anything. Null on any
 * failure (no WebGL, extension withheld, a throwing driver). The context is
 * released right away so it does not hold a GPU slot.
 */
export function collectGpuInfo(doc = typeof document !== "undefined" ? document : undefined) {
  try {
    const canvas = doc.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return null;
    let info = null;
    try {
      const ext = gl.getExtension("WEBGL_debug_renderer_info");
      if (ext) {
        info = {
          gpu_vendor: str(gl.getParameter(ext.UNMASKED_VENDOR_WEBGL), 128),
          gpu_renderer: str(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL), 128),
        };
      }
    } finally {
      try {
        const lose = gl.getExtension("WEBGL_lose_context");
        if (lose) lose.loseContext();
      } catch {
        /* nothing to release */
      }
    }
    return info;
  } catch {
    return null;
  }
}

function readTimezone() {
  try {
    return str(Intl.DateTimeFormat().resolvedOptions().timeZone, 64);
  } catch {
    return undefined;
  }
}

function readLanguages(nav) {
  try {
    const list = Array.isArray(nav?.languages) ? nav.languages : [];
    const out = list.map((l) => str(l, 16)).filter(Boolean).slice(0, 5);
    return out.length ? out : undefined;
  } catch {
    return undefined;
  }
}

/** Read one property defensively - some getters throw in locked-down browsers. */
function safe(read) {
  try {
    return read();
  } catch {
    return undefined;
  }
}

/**
 * Device traits shaped to the server allowlist.
 * @returns {object|null}
 */
export function collectDeviceTraits(env = {}) {
  const win = env.window || (typeof window !== "undefined" ? window : undefined);
  const nav = env.navigator || (typeof navigator !== "undefined" ? navigator : undefined);
  const scr = safe(() => win.screen);
  const gpu = env.gpu !== undefined ? env.gpu : collectGpuInfo(env.document);
  return compact({
    screen_w: num(safe(() => scr.width), 0, 20000, { integer: true }),
    screen_h: num(safe(() => scr.height), 0, 20000, { integer: true }),
    avail_w: num(safe(() => scr.availWidth), 0, 20000, { integer: true }),
    avail_h: num(safe(() => scr.availHeight), 0, 20000, { integer: true }),
    dpr: num(safe(() => win.devicePixelRatio), 0, 16),
    color_depth: num(safe(() => scr.colorDepth), 0, 64, { integer: true }),
    timezone: readTimezone(),
    languages: readLanguages(nav),
    hardware_concurrency: num(safe(() => nav.hardwareConcurrency), 0, 1024, { integer: true }),
    device_memory: num(safe(() => nav.deviceMemory), 0, 1024),
    max_touch_points: num(safe(() => nav.maxTouchPoints), 0, 256, { integer: true }),
    gpu_vendor: gpu?.gpu_vendor,
    gpu_renderer: gpu?.gpu_renderer,
    platform: str(safe(() => nav.platform), 32),
    cookie_enabled: bool(safe(() => nav.cookieEnabled)),
    pdf_viewer: bool(safe(() => nav.pdfViewerEnabled)),
  });
}

/**
 * The request body. Only the four allowlisted top-level keys, and only the
 * ones that have a value.
 */
export function buildPayload({ deviceId, context, clientHints, deviceTraits }) {
  const body = { context: SIGNAL_CONTEXTS.includes(context) ? context : "session" };
  if (deviceId) body.device_id = deviceId;
  if (clientHints) body.client_hints = clientHints;
  if (deviceTraits) body.device_traits = deviceTraits;
  return body;
}

// --- send --------------------------------------------------------------------------

/**
 * The three device fields, collected now. The id and the traits are
 * synchronous; only the high-entropy hints are awaited, for at most
 * `hintsTimeoutMs` (default 500 ms) - past that the low-entropy hints go alone.
 * `deviceTraits`, when given (an object or null), is used as is instead of
 * reading the traits again - the verify page reads them once at mount.
 */
async function collectDeviceFields({ hintsTimeoutMs, deviceTraits } = {}) {
  const deviceId = getOrCreateDeviceId();
  const clientHints = await collectClientHints(undefined, { timeoutMs: hintsTimeoutMs });
  const traits = deviceTraits !== undefined ? deviceTraits : collectDeviceTraits();
  return { deviceId, clientHints, deviceTraits: traits };
}

/**
 * The device fields for `POST /auth/verify-email` (`device_signals`):
 * `{ device_id?, client_hints?, device_traits? }`, or null when there is
 * nothing to send. No `context` (the server writes a signup row). Never
 * rejects; resolves within about `hintsTimeoutMs` (default 500 ms, the
 * client-hints cap). The verify page passes a shorter cap: it awaits this on
 * the user's code submit, where the hints are not worth a visible delay. It
 * also passes the `deviceTraits` it read at mount (collectDeviceTraits), so no
 * WebGL context is created on the submit; omitted, they are read here.
 */
export async function collectDeviceSignalsBody({ hintsTimeoutMs, deviceTraits } = {}) {
  try {
    const fields = buildPayload(await collectDeviceFields({ hintsTimeoutMs, deviceTraits }));
    delete fields.context;
    return Object.keys(fields).length ? fields : null;
  } catch {
    return null;
  }
}

/**
 * Collect and send once for `userId`, unless throttled. Resolves to what
 * happened ("sent" / "throttled" / "failed" / "skipped" / "cancelled") - for
 * tests; callers use `scheduleDeviceSignals` and ignore it. Never rejects.
 * `isCancelled` is checked right before the request goes out.
 */
export async function sendDeviceSignals({ userId, context = "session", isCancelled } = {}) {
  try {
    if (!userId) return "skipped";
    const key = String(userId);
    if (sentThisPageLoad.has(key) || isThrottled(key)) return "throttled";
    sentThisPageLoad.add(key);

    const { deviceId, clientHints, deviceTraits } = await collectDeviceFields();
    const payload = buildPayload({ deviceId, context, clientHints, deviceTraits });

    if (typeof isCancelled === "function" && isCancelled()) {
      // Signed out while collecting: nothing went out, so a later sign-in of
      // the same user in this page load may still send.
      sentThisPageLoad.delete(key);
      return "cancelled";
    }

    try {
      await api.post("/users/me/device-signals", payload);
      markSent(key);
      return "sent";
    } catch (error) {
      const status = error?.response?.status;
      // A 4xx will not heal by retrying on the next boot - stamp it so a
      // rejected body does not resend every page load. Network errors and
      // 5xx stay unstamped: the next boot (another page load) tries again.
      if (status >= 400 && status < 500) markSent(key);
      return "failed";
    }
  } catch {
    return "failed";
  }
}

/**
 * Fire-and-forget entry point: runs the send when the browser is idle.
 * Returns a cancel function (call it on sign-out; a no-op once the request has
 * gone out) and never throws. Nothing else cancels it - a re-render or a route
 * change right after sign-in must not lose the send.
 */
export function scheduleDeviceSignals({ userId, context } = {}) {
  let cancelled = false;
  let idleHandle = null;
  let timeoutHandle = null;
  const cancel = () => {
    cancelled = true;
    try {
      if (idleHandle !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
    } catch {
      /* the flag alone stops the send */
    }
  };
  try {
    if (!userId) return cancel;
    const run = () => {
      if (cancelled) return;
      sendDeviceSignals({ userId, context, isCancelled: () => cancelled }).catch(() => {});
    };
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      idleHandle = window.requestIdleCallback(run, { timeout: 5000 });
    } else {
      timeoutHandle = setTimeout(run, 1500);
    }
  } catch {
    /* scheduling itself failed - the beacon is best-effort */
  }
  return cancel;
}

/** Test hook: forget the in-memory per-page-load guard. */
export function __resetDeviceSignalsForTests() {
  sentThisPageLoad.clear();
}
